import {ModuleWithProviders, NgModule} from '@angular/core';
import {CommonModule, DecimalPipe} from '@angular/common';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {NgbCollapseModule, NgbTypeaheadModule} from '@ng-bootstrap/ng-bootstrap';

/*
 * Nebular imports
 */
import {
    NbAccordionModule,
    NbActionsModule,
    NbAlertModule,
    NbAutocompleteModule,
    NbButtonGroupModule,
    NbButtonModule,
    NbCalendarModule,
    NbCalendarRangeModule,
    NbCardModule,
    NbCheckboxModule,
    NbContextMenuModule,
    NbDatepickerModule,
    NbDialogModule,
    NbFormFieldModule,
    NbIconModule,
    NbInputModule,
    NbLayoutModule,
    NbListModule,
    NbMenuModule,
    NbPopoverModule,
    NbProgressBarModule,
    NbRadioModule,
    NbRouteTabsetModule,
    NbSelectModule,
    NbSidebarModule,
    NbSpinnerModule,
    NbStepperModule,
    NbTabsetModule,
    NbThemeModule,
    NbTimepickerModule,
    NbToastrModule,
    NbToggleModule,
    NbTooltipModule,
    NbUserModule,
    NbWindowModule,
} from '@nebular/theme';
import {NbEvaIconsModule} from '@nebular/eva-icons';
import {NbSecurityModule} from '@nebular/security';

/*
 * Components
 */
import * as components from './components';
import {DialogFilterCellComponent, EmptyDataComponent} from './components';

/*
 * Pipes
 */
import {
    CapitalizePipe,
    DateFormatPipe,
    DatetimeFormatPipe,
    GroupByPipe,
    NumberWithCommasPipe,
    PluralPipe,
    RoundPipe,
    SafePipe,
    SentenceCasePipe,
    ShowEnabledPipe,
    TimeLocalizePipe,
    TimeUnlocalizePipe,
    TimezonePipe,
    TimingPipe,
    TwoLevelLoopsPipe,
    UnitPercentPipe,
    UnitPipe,
} from './pipes';

/*
 * Directives
 */
import {DynamicComponentDirective, ThrottleClickDirective} from './directives';

/*
 * Empty components
 */
import {
    EmptyAreaChartComponent,
    EmptyBarChartStackedComponent,
    EmptyBarChartStackedSingleComponent,
    EmptyColumnChartComponent,
    EmptyKpiChartComponent,
    EmptyLabelValueVerticalComponent,
    EmptyLineChartComponent,
    EmptyPieChartComponent,
    EmptyPieChartDonutComponent,
    EmptyTableComponent,
    EmptyTableSmallComponent,
} from './empty-components';

/*
 * Styles and theme
 */
import {DEFAULT_THEME} from './styles/theme.default';
import {DARK_THEME} from './styles/theme.dark';
import {DEFAULT_ACCESSIBILITY_THEME} from './styles/theme.accessibility.default';
import {DARK_ACCESSIBILITY_THEME} from './styles/theme.accessibility.dark';
// Other
import {AuthModule} from '@auth/auth.module';
import {RouterModule} from '@angular/router';

// External
import {NumericRangeControlComponent} from '@theme/components/ui-form-components/numeric-range-control/numeric-range-control.component';
import {NgxEchartsModule} from 'ngx-echarts';
import {NgxValidationMessageComponent} from '@theme/components/ui-form-components/validation-message/validation-message.component';
import {ChartDataSortComponent} from '@theme/components/charts/chart-data-sort/chart-data-sort.component';
import {ChartDataSortPopupComponent} from '@theme/components/charts/chart-data-sort/chart-data-sort-popup.component';
import {ChartLegendBarComponent} from '@theme/components/charts/chart-legend-bar/chart-legend-bar.component';
import {ChartTitleBarComponent} from '@theme/components/charts/chart-title-bar/chart-title-bar.component';
import {NestedAccordionComponent} from '@theme/components/ui/nested-accordion/nested-accordion.component';
import {InlineRadioGroupComponent} from '@theme/components/ui-form-components/inline-radio-group/inline-radio-group.component';
import {NgxMiniLineChartComponent} from '@theme/components/charts/ngx-mini-line-chart/ngx-mini-line-chart.component';
import {PageHeaderComponent} from '@theme/components/layout/page-header/page-header.component';
import {DataAuditWidgetComponent} from '@theme/components/layout/data-audit-widget/data-audit-widget.component';
import {SystemInfoDialogComponent} from '@theme/components/layout/system-info-dialog/system-info-dialog.component';
import {DeviceDetectorService} from 'ngx-device-detector';
import {HelpSupportComponent} from '@theme/components/layout/help-support/help-support.component';
import {HelpSupportDialogComponent} from '@theme/components/layout/help-support/help-support-dialog/help-support-dialog.component';
import {HelpSupportSideBarComponent} from '@theme/components/layout/help-support/help-support-side-bar/help-support-side-bar.component';
import {AssetDetailsComponent, HeaderSettingsComponent, HeaderSettingsDialogComponent} from '@theme/components';
import {NgxSmartTableModule} from '@mominsamir/ngx-smart-table';
import {MiniDataTableComponent} from '@theme/components/tables/mini-data-table-card/mini-data-table/mini-data-table.component';
import {MiniTrendComponent} from '@theme/components/ui/mini-trend/mini-trend.component';
import {PageHeaderBreadcrumbComponent} from '@theme/components/layout/page-header-breadcrumb/page-header-breadcrumb.component';
import {WorkflowSelectorComponent} from '@theme/components/card-components/workflow-selector/workflow-selector.component';
import {PopoverMessageBoxComponent} from '@theme/components/ui/popover-message-box/popover-message-box.component';
import {TooltipComponent} from '@theme/components/ui/tooltip/tooltip.component';
import {PrevButtonComponent} from '@theme/components/tables/components/pager-button/prev-button/prev-button.component';
import {NextButtonComponent} from '@theme/components/tables/components/pager-button/next-button/next-button.component';
import {AccessibilitySwitcherComponent} from '@theme/components/layout/accessibility-switcher/accessibility-switcher.component';
import {UserNotificationComponent} from '@theme/components/user-notification/user-notification.component';
import {OnlyNumberDirective} from '@theme/directives/only-number.directive';
import {ChartSimpleLegendBarComponent} from '@theme/components/charts/chart-simple-legend-bar/chart-simple-legend-bar.component';
import {DashboardCardComponent} from '@theme/components/layout/dashboard/dashboard-card/dashboard-card.component';
import {TruncateTextDirective} from '@theme/directives/truncate-text.directive';
import {DashboardCardTextComponent} from '@theme/components/layout/dashboard/components/dashboard-card-text/dashboard-card-text.component';
import {DashboardCardNumberComponent} from '@theme/components/layout/dashboard/components/dashboard-card-number/dashboard-card-number.component';
import {DashboardCardTableComponent} from '@theme/components/layout/dashboard/components/dashboard-card-table/dashboard-card-table.component';
import {SpatialCardMetricSelectComponent} from '@theme/components/card-components/spatial-card/components/measure-metric/spatial-card-metric-select.component';
import {PointLayerService} from '@core/utils/engin/geospatial-viewer/esri-layers/engin-data/point-layer.service';
import {GenerateLayerService} from '@core/utils/engin/geospatial-viewer/esri-layers/generate-layer.service';
import {CoordinateConverterService} from '@core/utils/engin/geospatial-viewer/helper/coordinate-converter.service';
import {EnginDataPrepareService} from '@core/utils/engin/geospatial-viewer/helper/engin-data-prepare.service';
import {GeospatialService} from '@core/utils/engin/geospatial-viewer/helper/geospatial-service';
import {GeospatialViewerControlsStore} from '../pages/geospatial-viewer/api/geospatial-viewer-controls.store';
import {GeospatialFilterStore} from '../pages/geospatial-viewer/api/geospatial-filter.store';
import {AssetRendererService} from '@core/utils/engin/geospatial-viewer/renderers/asset-renderer.service';
import {AssetFieldsService} from '@core/utils/engin/geospatial-viewer/fields/asset-fields.service';
import {PolylineLayerService} from '@core/utils/engin/geospatial-viewer/esri-layers/engin-data/polyline-layer.service';
import {PolygonLayerService} from '@core/utils/engin/geospatial-viewer/esri-layers/engin-data/polygon-layer.service';
import {AssetPolygonRendererService} from '@core/utils/engin/geospatial-viewer/renderers/asset-polygon-renderer.service';
import {ProjectLayerService} from '@core/utils/engin/geospatial-viewer/esri-layers/custom-layer/project-layer.service';
import {SymbolLayerService} from '@core/utils/engin/geospatial-viewer/esri-layers/engin-data/symbol-layer.service';
import {ProjectFieldsService} from '@core/utils/engin/geospatial-viewer/fields/project-fields.service';
import {ProjectRendererService} from '@core/utils/engin/geospatial-viewer/renderers/project-renderer.service';
import {GeospatialViewerService} from '../pages/geospatial-viewer/api/geospatial-viewer.service';
import {GeospatialViewerApi} from '../pages/geospatial-viewer/api/geospatial-viewer.api';
import {GeospatialViewerControlsService} from '../pages/geospatial-viewer/api/geospatial-viewer-controls.service';
import {GeospatialViewerControlsApi} from '../pages/geospatial-viewer/api/geospatial-viewer-controls.api';
import {ChartKpiComponent} from '@theme/components/charts/chart-kpi/chart-kpi.component';
import {ChartKpiContainerComponent} from '@theme/components/charts/chart-kpi/chart-kpi-container/chart-kpi-container.component';
import {TabButtonsComponent} from '@theme/components/ui/tab-buttons/tab-buttons.component';
import {NgxFileDropModule} from 'ngx-file-drop';
import {TagNoteEditDialogComponent} from './components/dialogs/tag-note-edit-dialog/tag-note-edit-dialog.component';
import {ActionCellComponent} from '@theme/components/tables/data-table/cells/action-cell/action-cell.component';
import {CustomizedDeleteComponent} from './components/dialogs/customized-delete/customized-delete.component';
import {LogDialogComponent} from './components/dialogs/log-dialog/log-dialog.component';
import {ClickOutsideDirective} from '@theme/directives/click-outside.directive';
import {NbAuthModule} from '@nebular/auth';
import {FiltersAccordionComponent} from '@theme/components/layout/control-panel/filters-accordion/filters-accordion.component';
import {PreprocessDialogComponent} from '@theme/components/dialogs/preprocess-dialog/preprocess-dialog.component';
import {ScopeCellComponent} from '@theme/components/tables/data-table/cells/scope-cell/scope-cell.component';
import {ExpandRowComponent} from '@theme/components/tables/data-table/cells/action-cell/expand-row.component';
import {FormSectionComponent} from '@theme/components/card-components/inspection-form/form-section/form-section.component';
import {FormCanvasComponent} from '@theme/components/card-components/inspection-form/form-canvas/form-canvas.component';
import {FormCellComponent} from '@theme/components/card-components/inspection-form/form-cell/form-cell.component';
import {TextComponent} from '@theme/components/card-components/inspection-form/cells/text/text.component';
import {NumericComponent} from '@theme/components/card-components/inspection-form/cells/numeric/numeric.component';
import {SingleSelectComponent} from '@theme/components/card-components/inspection-form/cells/single-select/single-select.component';
import {MultiSelectComponent} from '@theme/components/card-components/inspection-form/cells/multi-select/multi-select.component';
import {MediaComponent} from '@theme/components/card-components/inspection-form/cells/media/media.component';
import {SignatureComponent} from '@theme/components/card-components/inspection-form/cells/signature/signature.component';
import {CellHeaderComponent} from '@theme/components/card-components/inspection-form/cells/cell-header/cell-header.component';
import {UploadMediaDialogComponent} from './components/dialogs/upload-images-dialog/upload-media-dialog.component';
import {MediaCarouselDialogComponent} from '@theme/components/dialogs/image-carousel/image-carousel-dialog/media-carousel-dialog.component';
import {StepperComponent} from '@theme/components/ui/stepper/stepper.component';
import {InputFilterComponent} from '@theme/components/tables/data-table/custom-filters/input-filter.component';
import {FileValidateComponent} from '@theme/components/card-components/file-validate/file-validate.component';
import {FileNameWithIconsComponent} from '@theme/components/card-components/file-validate/file-name-with-icon/file-name-with-icon';
import {ActionColumnComponent} from '@theme/components/card-components/file-validate/action-column/action-column';
import {FullImageDialogComponent} from '@theme/components/dialogs/image-carousel/image-carousel-dialog/full-image-dialog/full-image-dialog.component';
import {SignatureDialogComponent} from '@theme/components/dialogs/signature-dialog/signature-dialog.component';
import {SignaturePadModule} from 'angular2-signaturepad';
import {CheckboxComponent} from '@theme/components/card-components/inspection-form/cells/checkbox/checkbox.component';
import {TableComponent} from '@theme/components/card-components/inspection-form/cells/table/table.component';
import {IssuesComponent} from '@theme/components/card-components/inspection-form/cells/issues/issues.component';
import {InputBoxByTypeComponent} from '@theme/components/ui-form-components/input-box-by-type/input-box-by-type.component';
import {InputWithSearchBoxFormComponent} from '../pages/maintenance-planning/inspection-rules/components/input-with-search-box-form/input-with-search-box-form.component';
import {LayersService} from '../pages/geospatial-viewer/layers/layers.service';
import {GenericPopupService} from '@core/utils/engin/geospatial-viewer/popups/generic-popup.service';
import {AlertMessageInlineComponent} from '@theme/components/ui/alert-message-inline/alert-message-inline.component';
import {TrimInputDirective} from '@theme/directives/input-trim.directive';
import {TagComponent} from '@theme/components';
import {SpatialCardCategoryFiltersComponent} from '@theme/components/card-components/spatial-card/components/category-filters/spatial-card-category-filters.component';
import {SubmitPopoverLayoutComponent} from '@theme/components';
import {ScrollLockDirective} from '@theme/directives/scroll-lock.directive';
import {OutagePolylineLayerService} from '@core/utils/engin/geospatial-viewer/esri-layers/engin-data/outage-polyline-layer.service';
import {OutagePointLayerService} from '@core/utils/engin/geospatial-viewer/esri-layers/engin-data/outage-point-layer.service';
import {OutageRendererService} from '@core/utils/engin/geospatial-viewer/renderers/outage-renderer.service';
import {OutageFieldsService} from '@core/utils/engin/geospatial-viewer/fields/outage-fields.service';
import {FormatNumberDirective} from './directives/format-number.directive';
import {PopupSelectComponent} from './components/ui/popup-select/popup-select.component';
import {ColoredMetricCellComponent} from './components/tables/data-table/cells/map-legend-cell/colored-metric-cell.component';

const BASE_MODULES = [CommonModule, FormsModule, ReactiveFormsModule];
const NB_MODULES = [
    NbEvaIconsModule,
    NbCardModule,
    NbLayoutModule,
    NbTabsetModule,
    NbRouteTabsetModule,
    NbMenuModule,
    NbUserModule,
    NbActionsModule,
    NbSidebarModule,
    NbCheckboxModule,
    NbPopoverModule,
    NbContextMenuModule,
    NgbCollapseModule,
    NgbTypeaheadModule,
    NbSecurityModule, // *nbIsGranted directive,
    NbProgressBarModule,
    NbCalendarModule,
    NbCalendarRangeModule,
    NbStepperModule,
    NbButtonModule,
    NbButtonGroupModule,
    NbListModule,
    NbToastrModule,
    NbInputModule,
    NbFormFieldModule,
    NbAccordionModule,
    NbDatepickerModule,
    NbDialogModule,
    NbWindowModule,
    NbAlertModule,
    NbSpinnerModule,
    NbRadioModule,
    NbSelectModule,
    NbTooltipModule,
    NbToggleModule,

    NbIconModule,
    NgxEchartsModule,
    NbAutocompleteModule,
];
const APP_MODULES = [NgxSmartTableModule, NgxFileDropModule];

const COMPONENTS = [
    // Auth
    components.PasswordResetComponent,
    components.PasswordForceComponent,
    components.PasswordEmptySsoComponent,
    components.ChangePasswordComponent,
    components.InputPasswordComponent,
    components.CustomNbAuthComponent,

    NgxValidationMessageComponent,
    NgxValidationMessageComponent,
    components.ThemeSwitcherComponent,
    components.FooterComponent,
    components.ValueBoxComponent,
    components.CheckboxGroupComponent,
    components.DateRangePickerComponent,
    components.IconBoxComponent,
    components.PagesGroupPopoverComponent,
    components.MenuComponent,
    components.SubmenuComponent,
    components.SubmenuPopoverComponent,
    components.TablePagerComponent,
    components.SidebarActionComponent,
    components.SidebarActionPopoverComponent,
    components.ChartComponent,
    components.ChartSettingsComponent,
    components.TableSettingsPopupComponent,
    components.ChartSettingsPopupComponent,
    components.SliderComponent,
    components.DataTableComponent,
    components.DateFilterComponent,
    components.FilterFieldComponent,
    components.GenericFilterComponent,
    components.StringDateFilterComponent,
    components.NumberInputBoxComponent,
    components.NumericRangeFilterComponent,
    components.ConfirmDialogComponent,
    components.ReportFilterDialogComponent,
    components.InputErrorComponent,
    components.NumberInputComponent,
    components.EmptyDataComponent,
    components.StatusCellComponent,
    components.TruncatedTextCellComponent,
    components.LinkCellComponent,
    components.AnchorLinkCellComponent,
    components.CustomClassComponent,
    components.FileCellComponent,
    components.TableSettingsComponent,
    components.TableSettingsPopupComponent,
    components.MultiSelectWithSearchFilterComponent,
    components.MultiSelectFilterComponent,
    components.AlertStatusCellComponent,
    components.CheckBoxCellComponent,
    components.CheckBoxSelectAllComponent,
    components.RadioBoxCellComponent,
    components.ToggleCellComponent,
    components.DropdownCellComponent,
    components.CurrentPositionVisualizerComponent,
    components.GuideDialogComponent,
    components.EditNoteDialogComponent,
    components.UploadFileDialogComponent,
    TabButtonsComponent,
    components.StringDateFilterComponent,
    components.TextWithIconCellComponent,
    components.DialogFilterCellComponent,

    components.ChangePasswordComponent,
    components.InputPasswordComponent,
    components.MultiselectAutocompleteComponent,
    components.SingleSelectAutocompleteComponent,
    components.AssetDocumentsComponent,
    components.DocumentPreviewComponent,
    components.DocumentListComponent,
    components.DocumentViewCellComponent,

    components.ButtonMultiselectComponent,
    components.NotesComponent,
    components.AssetDetailsComponent,
    components.AlertStatusComponent,
    components.StatusComponent,
    components.GenericFilterComponent,
    components.ApiCheckboxGroupComponent,
    components.SimpleCheckboxGroupComponent,
    components.GenericFilterAccordionComponent,
    components.GenericFilterGroupAccordionComponent,
    components.NestedSelectDropdownComponent,
    components.SubmitPopoverLayoutComponent,

    components.SelectComponent,
    components.TagComponent,

    components.SubmitWithCommentComponent,
    components.SideBarComponent,
    components.DeficiencyAlertButtonComponent,

    HelpSupportComponent,
    HelpSupportDialogComponent,
    HelpSupportSideBarComponent,
    HeaderSettingsComponent,
    HeaderSettingsDialogComponent,

    // General
    PageHeaderComponent,
    PageHeaderBreadcrumbComponent,
    PopoverMessageBoxComponent,
    TooltipComponent,
    PrevButtonComponent,
    NextButtonComponent,
    UserNotificationComponent,
    components.SidePanelComponent,

    // General, related to data audit tool
    InlineRadioGroupComponent,
    NestedAccordionComponent,
    NgxMiniLineChartComponent,
    ChartDataSortComponent,
    ChartDataSortPopupComponent,
    ChartTitleBarComponent,
    ChartLegendBarComponent,
    ChartSimpleLegendBarComponent,
    DataAuditWidgetComponent,
    MiniTrendComponent,

    // General, related to reporting tool
    WorkflowSelectorComponent,

    //Data table Card
    MiniDataTableComponent,

    // Empty chart components
    EmptyAreaChartComponent,
    EmptyBarChartStackedComponent,
    EmptyBarChartStackedSingleComponent,
    EmptyColumnChartComponent,
    EmptyKpiChartComponent,
    EmptyLabelValueVerticalComponent,
    EmptyLineChartComponent,
    EmptyPieChartComponent,
    EmptyPieChartDonutComponent,
    EmptyTableComponent,
    EmptyTableSmallComponent,
    components.CustomAutocompleteComponent,

    // Spatial Card Components
    components.SpatialCardAssetsComponent,
    components.SpatialCardMetricsComponent,
    components.SpatialCardProjectsComponent,

    components.UploadFileCardComponent,
    components.DropFileComponent,
    components.DisplayFileComponent,

    components.RadioCardComponent,
    components.SensitiveInfoDisclosureLoginComponent,
    components.SensitiveInfoContentsComponent,
    components.SensitiveInfoDisclosureHeaderComponent,

    components.ControlPanelCheckboxGroupComponent,
    components.NumericRangeControlComponent,

    components.FormComponent,
];

const ENTRY_COMPONENTS = [EmptyDataComponent];

const PIPES = [
    CapitalizePipe,
    PluralPipe,
    RoundPipe,
    TimingPipe,
    NumberWithCommasPipe,
    SafePipe,
    ShowEnabledPipe,
    TimezonePipe,
    GroupByPipe,
    TwoLevelLoopsPipe,
    UnitPipe,
    UnitPercentPipe,
    SentenceCasePipe,
    DateFormatPipe,
    DatetimeFormatPipe,
];
const DATA_PIPES = [TimeLocalizePipe, TimeUnlocalizePipe];

const DIRECTIVES = [
    DynamicComponentDirective,
    FormatNumberDirective,
    OnlyNumberDirective,
    TruncateTextDirective,
    ThrottleClickDirective,
    ClickOutsideDirective,
    TrimInputDirective,
    ScrollLockDirective,
];
const GEOSPATIAL_VIEWER_SERVICE = [
    //service
    PointLayerService,
    GenerateLayerService,
    CoordinateConverterService,
    EnginDataPrepareService,
    GeospatialService,
    GenericPopupService,
    LayersService,
    GeospatialViewerControlsStore,
    GeospatialFilterStore,
    AssetRendererService,
    AssetFieldsService,
    PolylineLayerService,
    PolygonLayerService,
    AssetPolygonRendererService,
    ProjectLayerService,
    SymbolLayerService,
    ProjectFieldsService,
    ProjectRendererService,

    OutagePolylineLayerService,
    OutagePointLayerService,
    OutageRendererService,
    OutageFieldsService,

    //APi
    GeospatialViewerService,
    GeospatialViewerApi,
    GeospatialViewerControlsService,
    GeospatialViewerControlsApi,
];

const NB_THEME_PROVIDERS = [
    ...NbThemeModule.forRoot(
        {
            name: 'default',
        },
        // [DEFAULT_THEME, DARK_THEME],
        [DEFAULT_THEME, DARK_THEME, DEFAULT_ACCESSIBILITY_THEME, DARK_ACCESSIBILITY_THEME],
    ).providers,
    ...NbSidebarModule.forRoot().providers,
    ...NbMenuModule.forRoot().providers,
    ...NbDatepickerModule.forRoot().providers,
    ...NbTimepickerModule.forRoot().providers,
    ...NbDialogModule.forRoot().providers,
    ...NbWindowModule.forRoot().providers,
    ...NbToastrModule.forRoot().providers,
];

@NgModule({
    imports: [
        AuthModule,
        RouterModule,
        ...BASE_MODULES,
        ...NB_MODULES,
        ...APP_MODULES,
        NgxEchartsModule.forRoot({
            echarts: () => import('echarts'),
        }),
        NbAuthModule,
        SignaturePadModule,
    ],
    exports: [
        ...BASE_MODULES,
        ...NB_MODULES,
        ...COMPONENTS,
        ...PIPES,
        ...DATA_PIPES,
        ...DIRECTIVES,
        ...APP_MODULES,
        DashboardCardComponent,
        DashboardCardTextComponent,
        DashboardCardNumberComponent,
        DashboardCardTableComponent,
        ChartKpiComponent,
        ChartKpiContainerComponent,
        NumericRangeControlComponent,
        FiltersAccordionComponent,
        StepperComponent,
        InputFilterComponent,
        FileValidateComponent,
        FileNameWithIconsComponent,
        ActionColumnComponent,
        InputWithSearchBoxFormComponent,
        AlertMessageInlineComponent,
        SpatialCardCategoryFiltersComponent,
        PopupSelectComponent,
    ],
    providers: [DecimalPipe, DeviceDetectorService, ...GEOSPATIAL_VIEWER_SERVICE],
    declarations: [
        ...COMPONENTS,
        ...PIPES,
        ...DATA_PIPES,
        ...DIRECTIVES,
        SystemInfoDialogComponent,
        AccessibilitySwitcherComponent,
        DashboardCardComponent,
        DashboardCardTextComponent,
        DashboardCardNumberComponent,
        DashboardCardTableComponent,
        SpatialCardMetricSelectComponent,
        SpatialCardCategoryFiltersComponent,
        ChartKpiComponent,
        ChartKpiContainerComponent,
        SystemInfoDialogComponent,
        NumericRangeControlComponent,
        TagNoteEditDialogComponent,
        ActionCellComponent,
        CustomizedDeleteComponent,
        LogDialogComponent,
        PreprocessDialogComponent,
        FiltersAccordionComponent,
        AssetDetailsComponent,
        ExpandRowComponent,
        ScopeCellComponent,
        FormSectionComponent,
        FormCanvasComponent,
        FormCellComponent,
        TextComponent,
        NumericComponent,
        CheckboxComponent,
        SingleSelectComponent,
        MultiSelectComponent,
        MediaComponent,
        UploadMediaDialogComponent,
        MediaCarouselDialogComponent,
        SignatureComponent,
        CellHeaderComponent,
        SignatureDialogComponent,
        StepperComponent,
        FileValidateComponent,
        FileNameWithIconsComponent,
        ActionColumnComponent,
        InputFilterComponent,
        FullImageDialogComponent,
        TableComponent,
        IssuesComponent,
        InputBoxByTypeComponent,
        InputWithSearchBoxFormComponent,
        AlertMessageInlineComponent,
        TagComponent,
        SubmitPopoverLayoutComponent,
        PopupSelectComponent,
        ColoredMetricCellComponent,
    ],
    entryComponents: [...ENTRY_COMPONENTS],
})
export class ThemeModule {
    static forRoot(): ModuleWithProviders<ThemeModule> {
        return {
            ngModule: ThemeModule,
            providers: [
                ...NB_THEME_PROVIDERS,
                DateFormatPipe,
                DatetimeFormatPipe,
                TimeLocalizePipe,
                TimeUnlocalizePipe,
                NumberWithCommasPipe,
            ],
        };
    }
}
